// A payment term's schedule is a list of rows: what share of the amount is due
// and how many days after delivery/invoice. Terms created by the first seed
// stored it as an object ({ advance: 50, delivery: 50 }); normalizePaymentSchedule
// reads both so nothing breaks on old data.

export interface PaymentScheduleRow {
  /** Share of the total, 0-100. */
  percentage: number
  /** Days until it is due; 0 means on delivery / up front. */
  days: number
  description?: string
}

const toNumber = (value: unknown, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

const titleCase = (key: string) =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())

/** Days and label for a key of the old object form. */
function rowFromLegacyKey(key: string, percentage: number): PaymentScheduleRow {
  const lower = key.toLowerCase()
  const net = lower.match(/^net[\s_-]?(\d+)$/)
  if (net) return { percentage, days: Number(net[1]), description: `Net ${net[1]}` }
  if (lower === 'milestone') return { percentage, days: 30, description: 'Milestone' }
  if (lower === 'delivery') return { percentage, days: 0, description: 'On delivery' }
  if (lower === 'advance') return { percentage, days: 0, description: 'Advance' }
  return { percentage, days: 0, description: titleCase(key) }
}

/** Always a list of rows, whether the stored schedule is a list, the old object, or empty. */
export function normalizePaymentSchedule(schedule: unknown): PaymentScheduleRow[] {
  if (Array.isArray(schedule)) {
    return schedule
      .filter((row) => row && typeof row === 'object')
      .map((row: any) => ({
        percentage: toNumber(row.percentage ?? row.percent ?? row.pct),
        days: toNumber(row.days),
        description: row.description === undefined || row.description === null ? '' : String(row.description),
      }))
  }
  if (schedule && typeof schedule === 'object') {
    return Object.entries(schedule as Record<string, unknown>).map(([key, value]) => rowFromLegacyKey(key, toNumber(value)))
  }
  return []
}
